import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SignInModal } from '../components/SignInModal';
import { SignUpModal } from '../components/SignUpModal';
import { SEO } from '../components/common/SEO';
import { useCanonicalUrl } from '../hooks/useCanonicalUrl';
import { H1, H2, H4, Body, BodyLarge, H6, H5 } from '../components/ui/Typography';

const NewHomePage: React.FC = () => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const canonicalUrl = useCanonicalUrl();

  return (
    <>
      <SEO 
        title="Wishful - The easiest way to create, share and receive gifts"
        description="Create wishlists, plan gifts, track prices, and organize holiday celebrations. The ultimate platform for gift planning and holiday organization."
        url={canonicalUrl}
      />
      
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white h-[64px] px-4 md:px-[60px] py-3">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo-wordmark.svg" alt="Wishful" className="h-7" />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#780000] font-bold text-sm tracking-tight hover:text-[#c1121f] transition-colors" style={{ fontFamily: 'Noto Sans, sans-serif' }}>
                Wishlists
              </Link>
              <Link to="/secret-santa-generator" className="text-[#780000] font-bold text-sm tracking-tight hover:text-[#c1121f] transition-colors" style={{ fontFamily: 'Noto Sans, sans-serif' }}>
                Secret Santa Generator
              </Link>
              <Link to="/price-tracker" className="text-[#780000] font-bold text-sm tracking-tight hover:text-[#c1121f] transition-colors" style={{ fontFamily: 'Noto Sans, sans-serif' }}>
                Price Tracker
              </Link>
              <Link to="/holiday-calendar-2025" className="text-[#780000] font-bold text-sm tracking-tight hover:text-[#c1121f] transition-colors" style={{ fontFamily: 'Noto Sans, sans-serif' }}>
                Holiday Calendar
              </Link>
              <Link to="/blog" className="text-[#780000] font-bold text-sm tracking-tight hover:text-[#c1121f] transition-colors" style={{ fontFamily: 'Noto Sans, sans-serif' }}>
                Blog
              </Link>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSignInModalOpen(true)}
                className="bg-white border-2 border-[#780000] text-[#780000] font-bold px-5 py-2 h-10 rounded-lg hover:bg-[#FEF1EE] transition-colors text-sm tracking-tight"
                style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsSignUpModalOpen(true)}
                className="bg-[#c1121f] text-white font-bold px-5 py-2 h-10 rounded-lg hover:bg-[#a00e1a] transition-colors text-sm tracking-tight"
                style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Get Started
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-[40px]">
          <div className="max-w-[1440px] mx-auto px-[60px]">
            <div className="flex items-center justify-center gap-[50px]">
              {/* Left Content - Exact Figma Specs */}
              <div className="flex flex-col gap-[48px] items-start max-w-[844px]">
                <H1 className="max-w-[685px]">
                  The easiest way to create, share and receive gifts.
                </H1>
                <BodyLarge className="text-black max-w-[844px]">
                  Create wishlists, plan gifts, track prices, and organize holiday celebrations. The ultimate platform for gift planning and holiday organization.
                </BodyLarge>
                <div className="flex gap-[25px] h-[45px] items-center">
                  <button
                    onClick={() => setIsSignInModalOpen(true)}
                    className="bg-white border-[3px] border-[#780000] text-[#780000] font-bold px-[60px] py-[9px] h-[45px] rounded-[12px] hover:bg-[#FEF1EE] transition-colors text-[15.5px] tracking-[-0.01px] w-[213px]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setIsSignUpModalOpen(true)}
                    className="bg-[#c1121f] text-white font-bold px-[60px] py-[9px] h-[45px] rounded-[12px] hover:bg-[#a00e1a] transition-colors text-[15.5px] tracking-[-0.01px] w-[213px]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    Get Started
                  </button>
                </div>
              </div>

              {/* Right Image */}
              <div className="h-[540px] w-[420px] rounded-[20px] overflow-hidden">
                <img 
                  src="/images/blog/hero-image.jpg" 
                  alt="People celebrating with gifts and food" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-[40px] bg-gray-50">
          <div className="max-w-[1440px] mx-auto px-[60px]">
            <div className="flex flex-col gap-[50px] items-center justify-center">
              <div className="flex flex-col gap-[48px] items-center justify-center max-w-[710px] text-center text-[#780000]">
                <H2 className="max-w-[674px]">
                  Here's how the magic works
                </H2>
                <H5 className="w-full">
                  Four easy steps to making your wishes come true
                </H5>
              </div>

              <div className="flex gap-[41px] items-center max-w-[1317px] mx-auto">
                {/* Step 1 */}
                <div className="bg-white rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-[300px] h-[400px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', flexShrink: 0 }}>
                  <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                    📋
                  </p>
                  <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                    <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                      Paste a URL
                    </h4>
                    <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      Copy any product link from your favorite online store
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-white rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-[300px] h-[400px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', flexShrink: 0 }}>
                  <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                    ✨
                  </p>
                  <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                    <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                      We do the magic
                    </h4>
                    <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      We automatically fetch product details, images, and pricing
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="bg-white rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-[300px] h-[400px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', flexShrink: 0 }}>
                  <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                    💅
                  </p>
                  <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                    <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                      Curate your list
                    </h4>
                    <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      Organize items, add notes, make it uniquely yours
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="bg-white rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-[300px] h-[400px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', flexShrink: 0 }}>
                  <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                    🎉
                  </p>
                  <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                    <h4 className="text-center w-full text-[#780000] whitespace-pre-wrap" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                      Share & enjoy
                    </h4>
                    <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      Send to your loved ones and watch your wishes come true
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-[#FEF1EE] rounded-[20px] max-w-[1319px]" style={{ display: 'flex', padding: '40px 49px', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', alignSelf: 'stretch' }}>
                <H2 className="max-w-[820px]">
                  Ready to get gifts you actually want?
                </H2>
                <button className="bg-white border-[3px] border-[#780000] text-[#780000] font-bold px-[30px] py-[9px] h-[45px] rounded-[12px] hover:bg-[#FEF1EE] transition-colors text-[15.5px] tracking-[-0.01px] w-[316px]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
                  Start Wishing (It's free!)
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-[40px]">
          <div className="max-w-[1440px] mx-auto px-[60px]">
            <div className="flex flex-col gap-[48px] items-start mb-8 md:mb-12">
              <H2 className="max-w-[674px]">
                Features that make gifting easy
              </H2>
              <div className="flex flex-col justify-center min-w-full relative shrink-0 w-[min-content]">
                <H4 className="whitespace-pre-wrap">
                  Everything<span> you need to start spreading  some joy</span>
                </H4>
              </div>
            </div>

            {/* Feature Grid - Complex Layout */}
            <div className="relative h-[867px] max-w-[1317px] mx-auto">
              {/* Column 1 */}
              <div className="absolute h-[772px] w-[252px] left-0 top-0">
                {/* Image 1 - Top */}
                <div className="absolute h-[376px] w-full top-0 rounded-lg overflow-hidden">
                  <img 
                    src="http://localhost:3845/assets/852396db86f13ec328916a58c5f95435cb718c59.png"
                    alt="Never forget another birthday"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay Card 3 - Bottom */}
                <div className="absolute bg-[rgba(102,155,188,0.8)] h-[376px] w-full top-[396px] rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="absolute flex flex-col gap-[61px] h-[292px] items-center left-[17px] text-white top-[42px] w-[217px]">
                    <h3 className="font-black text-[36px] leading-[35px] tracking-[-1.08px] w-full" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5" }}>
                      Create lists people actually use
                    </h3>
                    <p className="text-[18px] leading-normal w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      No more screenshots. Build beautiful wishlists that friends and family will love shopping from.
                    </p>
                  </div>
                </div>
              </div>

              {/* Column 2 */}
              <div className="absolute h-[772px] w-[252px] left-[266px] top-[95px]">
                {/* Overlay Card 1 - Top */}
                <div className="absolute bg-[rgba(0,48,73,0.8)] h-[376px] w-full top-0 rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="absolute flex flex-col gap-[61px] h-[292px] items-center left-[17px] text-white top-[42px] w-[217px]">
                    <h3 className="font-black text-[36px] leading-[35px] tracking-[-1.08px] w-full" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5" }}>
                      Never forget another birthday
                    </h3>
                    <p className="text-[18px] leading-normal w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      We'll remind you before it's too late to order something decent.
                    </p>
                  </div>
                </div>
                {/* Image 4 - Bottom */}
                <div className="absolute h-[376px] w-full top-[395px] rounded-lg overflow-hidden">
                  <img 
                    src="http://localhost:3845/assets/814849201cab85f60f87b59a78bcca5405983155.png"
                    alt="Make gift exchanges actually fun"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Column 3 */}
              <div className="absolute h-[772px] w-[252px] left-[533px] top-0">
                {/* Image 2 - Top */}
                <div className="absolute h-[376px] w-full top-0 rounded-lg overflow-hidden">
                  <img 
                    src="http://localhost:3845/assets/e8f481967b52fa29a817e8340c18da7d6f3ab7fa.png"
                    alt="Find gifts that don't suck"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay Card 4 - Bottom */}
                <div className="absolute bg-[rgba(102,155,188,0.8)] h-[376px] w-full top-[395px] rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="absolute flex flex-col gap-[61px] h-[292px] items-center left-[17px] text-white top-[42px] w-[217px]">
                    <h3 className="font-black text-[36px] leading-[35px] tracking-[-1.08px] w-full" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5" }}>
                      Make gift exchanges actually fun
                    </h3>
                    <p className="text-[18px] leading-normal w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      Add names, we'll assign gifts. No awkward conversations, just perfect exchanges.
                    </p>
                  </div>
                </div>
              </div>

              {/* Column 4 */}
              <div className="absolute h-[772px] w-[252px] left-[799px] top-[95px]">
                {/* Overlay Card 2 - Top */}
                <div className="absolute bg-[rgba(0,48,73,0.8)] h-[376px] w-full top-0 rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="absolute flex flex-col gap-[61px] h-[292px] items-center left-[17px] text-white top-[42px] w-[217px]">
                    <h3 className="font-black text-[36px] leading-[35px] tracking-[-1.08px] w-full" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5" }}>
                      Find gifts that don't suck
                    </h3>
                    <p className="text-[18px] leading-normal w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      Stuck on what to get someone? Our guides have ideas people actually want.
                    </p>
                  </div>
                </div>
                {/* Image 5 - Bottom */}
                <div className="absolute h-[376px] w-full top-[395px] rounded-lg overflow-hidden">
                  <img 
                    src="http://localhost:3845/assets/5e9795e54e012cb5ae24f9181b2553238f32af9c.png"
                    alt="Stop paying full price"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Column 5 */}
              <div className="absolute h-[772px] w-[252px] left-[1065px] top-0">
                {/* Image 3 - Top */}
                <div className="absolute h-[376px] w-full top-0 rounded-lg overflow-hidden">
                  <img 
                    src="http://localhost:3845/assets/838e98ec89ec3116fcd4030850ea6fed886765f5.png"
                    alt="Create lists people actually use"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay Card 5 - Bottom */}
                <div className="absolute bg-[rgba(102,155,188,0.8)] h-[376px] w-full top-[395px] rounded-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="absolute flex flex-col gap-[61px] h-[292px] items-center left-[17px] text-white top-[42px] w-[217px]">
                    <h3 className="font-black text-[36px] leading-[35px] tracking-[-1.08px] w-full" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5" }}>
                      Stop paying full price
                    </h3>
                    <p className="text-[18px] leading-normal w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      We'll ping you when your wishlist items go on sale. More money, same great gifts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feedback Section */}
        <section className="py-[40px] bg-[#FEF1EE]">
          <div className="max-w-[1440px] mx-auto px-[60px]">
            <div className="flex flex-col gap-[50px] items-center justify-center">
              <div className="bg-[#FEF1EE] flex flex-col gap-[24px] items-center justify-center px-[48px] py-[40px] rounded-[20px] max-w-[1319px]">
                <div className="flex gap-[225px] items-center justify-center w-full">
                  <div className="flex flex-col gap-[48px] items-start">
                    <H2 className="text-center w-full">
                      Help us make your wishes come true
                    </H2>
                  </div>
                </div>
                <BodyLarge className="text-black text-center max-w-[844px]">
                  Got a feature you're wishing existed? A bug that's killing your vibe? Toss your ideas into our digital wishing well and help us build something magical.
                </BodyLarge>
                <button className="bg-[#c1121f] text-white font-bold px-[60px] py-[9px] h-[45px] rounded-[12px] hover:bg-[#a00e1a] transition-colors text-[15.5px] tracking-[-0.01px]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
                  Cast your wish
                </button>
                <div className="h-[400px] relative w-[980px]">
                  <div className="absolute bg-white h-[400px] left-0 top-0 w-[300px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', borderRadius: '20px', boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)' }}>
                    <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      ✨
                    </p>
                    <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                      <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                        Cast a wish
                      </h4>
                      <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                        What feature would make your gift-giving dreams come true?
                      </p>
                    </div>
                  </div>
                  <div className="absolute bg-white h-[400px] left-[340px] top-0 w-[300px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', borderRadius: '20px', boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)' }}>
                    <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      🗳️
                    </p>
                    <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                      <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                        Vote for features
                      </h4>
                      <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                        Vote on ideas and help us prioritize the good stuff.
                      </p>
                    </div>
                  </div>
                  <div className="absolute bg-white h-[400px] left-[680px] top-0 w-[300px]" style={{ display: 'flex', padding: '70px 23px 20px 23px', flexDirection: 'column', alignItems: 'center', gap: '53px', borderRadius: '20px', boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)' }}>
                    <p className="text-[100px] text-center text-neutral-950 w-full leading-normal" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                      🚀
                    </p>
                    <div style={{ display: 'flex', height: '144px', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                      <h4 className="text-center w-full text-[#780000]" style={{ fontFamily: 'Noto Sans, sans-serif', fontVariationSettings: "'CTGR' 100, 'wdth' 62.5", fontSize: '1.9rem', lineHeight: '1.35rem', letterSpacing: '-0.08rem', fontWeight: 900 }}>
                        Make magic
                      </h4>
                      <p className="font-normal text-[18px] leading-normal text-center w-full" style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100" }}>
                        Great ideas become real around here.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modals */}
        <SignInModal 
          isOpen={isSignInModalOpen} 
          onClose={() => setIsSignInModalOpen(false)}
          onOpenSignUp={() => {
            setIsSignInModalOpen(false);
            setIsSignUpModalOpen(true);
          }}
        />
        <SignUpModal 
          isOpen={isSignUpModalOpen} 
          onClose={() => setIsSignUpModalOpen(false)}
          onOpenSignIn={() => {
            setIsSignUpModalOpen(false);
            setIsSignInModalOpen(true);
          }}
        />
      </div>
    </>
  );
};

export default NewHomePage;
